/** Commissions and the payout (SPEC §6.1 item 12, §7.13).
 *
 * Non-admins only ever receive their own entries; the Worker enforces that, and
 * the screen simply does not offer the member filter. */
import { queryOptions } from "@tanstack/react-query";
import type { InferResponseType } from "hono/client";
import { readJson, rpc, type QueryParams } from "./api";
import { queryClient } from "./session";

export type CommissionEntry = InferResponseType<typeof rpc.api.commissions.$get>["items"][number];
export type CommissionFilters = QueryParams<typeof rpc.api.commissions.$get>;
export type CommissionRule = InferResponseType<typeof rpc.api["commission-rules"]["$get"]>["items"][number];

export const commissionEntriesQuery = (filters: CommissionFilters = {}) => queryOptions({
  queryKey: ["commissions", filters],
  queryFn: () => readJson(rpc.api.commissions.$get({ query: filters })).then(result => result.items),
  staleTime: 30_000,
});

export const commissionRulesQuery = queryOptions({
  queryKey: ["commission-rules"],
  queryFn: () => readJson(rpc.api["commission-rules"].$get()).then(result => result.items),
  staleTime: 5 * 60_000,
});

/** A CSV the browser downloads directly, so the session cookie carries the auth. */
export const commissionPayoutUrl = (status?: string) =>
  `/api/commissions/payout.csv${status ? `?status=${encodeURIComponent(status)}` : ""}`;

export const commissionApi = {
  /** `clientRequestId` makes the payout idempotent: a retried tap marks the same
   * entries paid once rather than twice. */
  pay: async (entryIds: string[], payoutRef?: string) => {
    const result = await readJson(rpc.api.commissions.pay.$post({
      json: { entryIds, clientRequestId: crypto.randomUUID(), ...(payoutRef ? { payoutRef } : {}) },
    }));
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["commissions"] }),
      queryClient.invalidateQueries({ queryKey: ["today"] }),
    ]);
    return result;
  },
};
